// Hooks for sharing rules management
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiRequest } from '../lib/api'

export interface SharingRule {
  id: string
  name: string
  description: string | null
  entity: string
  ruleType: 'owner_based' | 'criteria_based'
  shareFromRole: string | null
  criteriaField: string | null
  criteriaOperator: string | null
  criteriaValue: string | null
  shareWithType: 'role' | 'user'
  shareWithRole: string | null
  shareWithUserId: string | null
  accessLevel: 'read' | 'read_write'
  isActive: boolean
  priority: string
  createdAt: string
  updatedAt: string
}

export interface SharingRuleInput {
  name: string
  description?: string
  entity: string
  ruleType: 'owner_based' | 'criteria_based'
  shareFromRole?: string
  criteriaField?: string
  criteriaOperator?: string
  criteriaValue?: string
  shareWithType: 'role' | 'user'
  shareWithRole?: string
  shareWithUserId?: string
  accessLevel: 'read' | 'read_write'
  isActive?: boolean
  priority?: number
}

export interface EntityField {
  field: string
  label: string
  type: 'text' | 'number' | 'boolean'
}

export interface RoleOption {
  value: string
  label: string
}

// Fetch all sharing rules
export function useSharingRules() {
  return useQuery({
    queryKey: ['sharing-rules'],
    queryFn: () => apiRequest<{ data: SharingRule[] }>('/sharing-rules'),
  })
}

// Fetch sharing rules for a specific entity
export function useSharingRulesByEntity(entity: string) {
  return useQuery({
    queryKey: ['sharing-rules', 'entity', entity],
    queryFn: () => apiRequest<{ data: SharingRule[] }>(`/sharing-rules/entity/${entity}`),
    enabled: !!entity,
  })
}

// Fetch a single sharing rule
export function useSharingRule(id: string) {
  return useQuery({
    queryKey: ['sharing-rules', id],
    queryFn: () => apiRequest<SharingRule>(`/sharing-rules/${id}`),
    enabled: !!id,
  })
}

// Fetch available fields for an entity
export function useEntityFields(entity: string) {
  return useQuery({
    queryKey: ['sharing-rules', 'fields', entity],
    queryFn: () => apiRequest<{ data: EntityField[] }>(`/sharing-rules/meta/fields/${entity}`),
    enabled: !!entity,
  })
}

// Fetch available roles
export function useRoles() {
  return useQuery({
    queryKey: ['sharing-rules', 'roles'],
    queryFn: () => apiRequest<{ data: RoleOption[] }>('/sharing-rules/meta/roles'),
  })
}

// Create sharing rule
export function useCreateSharingRule() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: SharingRuleInput) =>
      apiRequest<SharingRule>('/sharing-rules', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sharing-rules'] })
    },
  })
}

// Update sharing rule
export function useUpdateSharingRule() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<SharingRuleInput> }) =>
      apiRequest<SharingRule>(`/sharing-rules/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sharing-rules'] })
    },
  })
}

// Delete sharing rule
export function useDeleteSharingRule() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<void>(`/sharing-rules/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sharing-rules'] })
    },
  })
}

// Toggle sharing rule active status
export function useToggleSharingRule() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<SharingRule>(`/sharing-rules/${id}/toggle`, {
        method: 'POST',
        body: JSON.stringify({}),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sharing-rules'] })
    },
  })
}
